import { useState } from "react";
import { ShieldCheck, ArrowRight, Check, Lock } from "lucide-react";
import { toast } from "sonner";
import { adpInitiate, adpSetStatus, adpSetSteps, adpSetEligibility, adpNotify, convertToEmployee } from "@/lib/api";

const STATUS_LABEL = {
  not_started: "Not Started", prehire_started: "Prehire Started", waiting_on_candidate: "Waiting on Candidate",
  personal_complete: "Personal Section Complete", employer_setup_in_progress: "Employer Setup In Progress",
  adp_onboarding_in_progress: "ADP Onboarding In Progress", complete: "ADP Setup Complete",
};
const STATUS_ORDER = Object.keys(STATUS_LABEL);
const PERSONAL = [
  { v: "not_sent", l: "Not sent" }, { v: "requested", l: "Requested" },
  { v: "completing", l: "Employee completing" }, { v: "complete", l: "Complete" },
];
const EMP_SECTIONS = [
  { k: "employment", l: "Employment" }, { k: "payroll", l: "Payroll" },
  { k: "taxJurisdiction", l: "Tax Jurisdiction" }, { k: "timeAttendance", l: "Time & Attendance (when applicable)" },
];

function fmt(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString(undefined, { day: "2-digit", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return iso; }
}
function StepDone({ mark }) {
  return mark ? <span className="adp-done" data-testid="adp-step-done"><Check size={13} /> {fmt(mark.at)} · {mark.by}</span> : null;
}

function ConvertModal({ candidate, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const [c1, setC1] = useState(false), [c2, setC2] = useState(false), [c3, setC3] = useState(false);
  const submit = async () => {
    if (!(c1 && c2 && c3)) return;
    setBusy(true);
    try {
      await convertToEmployee(candidate.registrationId, { by: "Make It Happen Philly", confirm: true });
      toast.success("Candidate converted to Employee.");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not convert to Employee."); }
    finally { setBusy(false); }
  };
  return (
    <div className="modal-scrim" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} data-testid="convert-modal" style={{ maxWidth: 520 }}>
        <div className="modal-head"><h2>Complete Onboarding / Convert to Employee</h2></div>
        <div className="modal-body">
          <p className="mut" style={{ marginTop: 0 }}>This is the final, MIHP-authorized conversion. Confirm each checkpoint:</p>
          <label className="ca-ack" data-testid="convert-check-mihp"><input type="checkbox" checked={c1} onChange={(e) => setC1(e.target.checked)} /><span>MIHP onboarding requirements are complete.</span></label>
          <label className="ca-ack" data-testid="convert-check-adp"><input type="checkbox" checked={c2} onChange={(e) => setC2(e.target.checked)} /><span>Required ADP employment setup is complete.</span></label>
          <label className="ca-ack" data-testid="convert-check-elig"><input type="checkbox" checked={c3} onChange={(e) => setC3(e.target.checked)} /><span>Required employment-eligibility verification has been completed.</span></label>
          <button className="mh-save" disabled={busy || !(c1 && c2 && c3)} onClick={submit} data-testid="convert-confirm-btn" style={{ marginTop: 12 }}>{busy ? "Converting…" : "Convert to Employee"}</button>
        </div>
      </div>
    </div>
  );
}

export function AdpEmploymentSetup({ candidate, reload }) {
  const c = candidate;
  const adp = c.adp;
  const [busy, setBusy] = useState(false);
  const [showConvert, setShowConvert] = useState(false);

  const shift = c.hireShift || c.employmentApproval?.shift;
  const info = {
    Name: c.name, "Personal email": c.email, "Hire / start date": c.startDateLabel || c.employmentApproval?.startDate,
    "Employer / worksite": c.employerName, "Final position": c.position || c.employmentApproval?.position,
    "Final shift": shift,
    "Base schedule": c.scheduleOverride || c.baseSchedule || c.employmentApproval?.baseSchedule,
    "Assigned supervisor": c.interviewOutcome?.supervisorLabel,
  };

  const run = async (fn) => { if (busy) return; setBusy(true); try { await fn(); await reload(); } catch (e) { toast.error(e?.response?.data?.detail || "Action failed."); } finally { setBusy(false); } };

  if (!adp) {
    return (
      <div className="adp-wrap" data-testid="adp-setup">
        <div className="adp-head"><h3>ADP Employment Setup</h3></div>
        <p className="mut" style={{ margin: "0 0 10px" }}>MIHP onboarding requirements are complete. Continue to the ADP Employment Setup handoff (tax, banking and I-9 are handled securely by ADP — not by MIHP).</p>
        <button className="onboard-cta" data-testid="continue-adp-btn" disabled={busy}
          onClick={() => run(async () => { await adpInitiate(c.registrationId, "Make It Happen Philly"); toast.success("ADP Employment Setup started (manual handoff)."); })}>
          Continue to ADP Employment Setup <ArrowRight size={15} style={{ verticalAlign: "-2px" }} />
        </button>
      </div>
    );
  }

  const notified = adp.candidateNotified;
  const canNotify = !!(adp.steps.prehireStarted || adp.steps.askNewHire);
  const eligComplete = adp.employmentEligibility?.status === "complete";
  const canConvert = adp.status === "complete" && eligComplete && !c.convertedToEmployee;
  const locked = !!c.convertedToEmployee;
  const busyOrLocked = busy || locked;

  return (
    <div className="adp-wrap" data-testid="adp-setup">
      <div className="adp-head">
        <h3>ADP Employment Setup</h3>
        <span className="adp-mode" data-testid="adp-mode">ADP Handoff Mode: {adp.integrationMode === "manual" ? "Manual" : "API"}</span>
        <span className={`adp-status s-${adp.status}`} data-testid="adp-status-pill">{STATUS_LABEL[adp.status]}</span>
        {c.convertedToEmployee && <span className="adp-status s-complete" data-testid="employee-badge">Employee ✓</span>}
      </div>

      {/* Candidate info MIHP already knows */}
      <div className="adp-info" data-testid="adp-info">
        {Object.entries(info).map(([k, v]) => (
          <div key={k} className="adp-kv"><span className="adp-k">{k}</span><span className="adp-v">{v || <span className="mut">—</span>}</span></div>
        ))}
      </div>
      <p className="secure-note" data-testid="adp-privacy-note"><ShieldCheck size={15} /> MIHP does not collect SSN, Tax ID, date of birth, legal address, banking, tax withholding, I-9 data, or emergency contacts. ADP collects those directly and securely from the employee.</p>

      {locked && <div className="adp-locked" data-testid="adp-locked-note"><Lock size={14} /> This candidate is now an Employee — ADP Employment Setup is locked to preserve the conversion record.</div>}

      {/* Manual workflow steps */}
      <div className="adp-steps">
        <div className="adp-step" data-testid="adp-step-prehire">
          <div className="adp-step-h"><strong>Step 1 — Start ADP Hire / Prehire</strong><StepDone mark={adp.steps.prehireStarted} /></div>
          <p className="mut">Use the candidate information above to start the person in ADP Workforce Now.</p>
          {!adp.steps.prehireStarted && <button className="btn sm" disabled={busyOrLocked} data-testid="adp-prehire-btn" onClick={() => run(() => adpSetSteps(c.registrationId, { prehireStarted: true }))}>Mark Prehire started</button>}
        </div>

        <div className="adp-step" data-testid="adp-step-ask">
          <div className="adp-step-h"><strong>Step 2 — Ask the New Hire</strong><StepDone mark={adp.steps.askNewHire} /></div>
          <p className="mut">Record that the ADP "Ask the New Hire" request has been initiated (the employee completes the Personal section themselves in ADP).</p>
          {!adp.steps.askNewHire && <button className="btn sm" disabled={busyOrLocked} data-testid="adp-ask-btn" onClick={() => run(() => adpSetSteps(c.registrationId, { askNewHire: true }))}>Mark Ask the New Hire initiated</button>}
        </div>

        <div className="adp-step" data-testid="adp-step-personal">
          <div className="adp-step-h"><strong>Step 3 — Employee Personal Information</strong></div>
          <p className="mut">Status tracking only — MIHP does not collect the underlying sensitive information.</p>
          <select className="ca-input" value={adp.steps.personal} disabled={busyOrLocked} data-testid="adp-personal-select"
            onChange={(e) => run(() => adpSetSteps(c.registrationId, { personal: e.target.value }))}>
            {PERSONAL.map((p) => <option key={p.v} value={p.v}>{p.l}</option>)}
          </select>
        </div>

        <div className="adp-step" data-testid="adp-step-employer">
          <div className="adp-step-h"><strong>Step 4 — MIHP Employer Setup</strong></div>
          <p className="mut">Track completion of ADP-controlled employer sections (not recreated in MIHP).</p>
          <div className="adp-checks">
            {EMP_SECTIONS.map((s) => (
              <label key={s.k} className="ca-ack" data-testid={`adp-emp-${s.k}`}>
                <input type="checkbox" checked={!!adp.steps.employerSetup[s.k]} disabled={busyOrLocked}
                  onChange={(e) => run(() => adpSetSteps(c.registrationId, { employerSetup: { [s.k]: e.target.checked } }))} />
                <span>{s.l}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="adp-step" data-testid="adp-step-w2">
          <div className="adp-step-h"><strong>Step 5 — ADP W-2 Onboarding</strong></div>
          <p className="mut">ADP runs the paid-employee W-2 onboarding (profile, tax withholding, payment/direct deposit, emergency contacts, documents/policies, and any future eI-9).</p>
          <div className="action-row">
            {!adp.steps.w2Initiated
              ? <button className="btn sm" disabled={busyOrLocked} data-testid="adp-w2-init-btn" onClick={() => run(() => adpSetSteps(c.registrationId, { w2Initiated: true }))}>Mark W-2 Onboarding initiated</button>
              : <StepDone mark={adp.steps.w2Initiated} />}
            {adp.steps.w2Initiated && !adp.steps.w2Complete && <button className="btn sm" disabled={busyOrLocked} data-testid="adp-w2-complete-btn" onClick={() => run(() => adpSetSteps(c.registrationId, { w2Complete: true }))}>Mark W-2 Onboarding complete</button>}
            {adp.steps.w2Complete && <StepDone mark={adp.steps.w2Complete} />}
          </div>
        </div>
      </div>

      {/* Candidate communication */}
      <div className="adp-box" data-testid="adp-notify-box">
        <strong>Candidate communication</strong>
        <p className="mut">Tell the candidate their ADP setup has started. MIHP does not generate ADP usernames, passwords, or registration links — ADP owns its own secure login.</p>
        <button className="btn sm" disabled={busyOrLocked || !canNotify} data-testid="adp-notify-btn" onClick={() => run(async () => { const r = await adpNotify(c.registrationId, "Make It Happen Philly"); toast.success(r.emailSent ? "ADP-started notice emailed to the candidate." : "ADP-started notice recorded (email not delivered)."); })}>Send "ADP setup started" notice</button>
        {!canNotify && <span className="mut" style={{ marginLeft: 8, fontSize: ".78rem" }}>Available after Step 1 or Step 2.</span>}
        {notified && <div className="mut" data-testid="adp-notified-log" style={{ marginTop: 6, fontSize: ".78rem" }}>Notified {fmt(notified.at)} by {notified.by} · email {notified.emailSent ? "sent" : "not delivered"}</div>}
      </div>

      {/* Overall operational status */}
      <div className="adp-box" data-testid="adp-status-box">
        <strong>ADP Employment Setup status <span className="mut">(Manually Confirmed)</span></strong>
        <p className="mut">Operational tracking layer — this is not an assertion that MIHP has queried ADP.</p>
        <select className="ca-input" value={adp.status} disabled={busyOrLocked} data-testid="adp-status-select"
          onChange={(e) => run(() => adpSetStatus(c.registrationId, { status: e.target.value }))}>
          {STATUS_ORDER.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
        </select>
      </div>

      {/* Employment eligibility — separate compliance indicator */}
      <div className="adp-box" data-testid="adp-eligibility-box">
        <strong>Employment Eligibility Verification</strong>
        <p className="mut">Kept separate from general ADP setup. ADP eI-9 availability is still being confirmed — no I-9 form or documents are collected in MIHP. An authorized MIHP user may confirm this manually for now.</p>
        <div className="action-row">
          <span className={`adp-status ${eligComplete ? "s-complete" : "s-not_started"}`} data-testid="adp-eligibility-pill">{eligComplete ? "Complete" : "Pending"}</span>
          <button className="btn sm" disabled={busyOrLocked} data-testid="adp-eligibility-toggle" onClick={() => run(() => adpSetEligibility(c.registrationId, { status: eligComplete ? "pending" : "complete" }))}>{eligComplete ? "Mark Pending" : "Mark Complete"}</button>
        </div>
        {adp.employmentEligibility?.at && <div className="mut" style={{ marginTop: 6, fontSize: ".78rem" }}>Last set {fmt(adp.employmentEligibility.at)} by {adp.employmentEligibility.by}</div>}
      </div>

      {/* API-readiness (mostly null in manual mode) */}
      <details className="adp-box" data-testid="adp-integration-box">
        <summary>Integration details (API-ready · currently manual)</summary>
        <div className="adp-kv"><span className="adp-k">Integration mode</span><span className="adp-v">{adp.integrationMode}</span></div>
        <div className="adp-kv"><span className="adp-k">External ADP reference</span><span className="adp-v">{adp.externalRef || <span className="mut">— (set when API-synced)</span>}</span></div>
        <div className="adp-kv"><span className="adp-k">Handoff initiated</span><span className="adp-v">{fmt(adp.handoffInitiatedAt)} · {adp.handoffInitiatedBy}</span></div>
        <div className="adp-kv"><span className="adp-k">Sync status</span><span className="adp-v">{adp.syncStatus || <span className="mut">— (manual)</span>}</span></div>
        <div className="adp-kv"><span className="adp-k">Last synchronization</span><span className="adp-v">{fmt(adp.lastSyncAt) || <span className="mut">—</span>}</span></div>
        <div className="adp-kv"><span className="adp-k">Integration error</span><span className="adp-v">{adp.integrationError || <span className="mut">—</span>}</span></div>
      </details>

      {/* Audit history */}
      <details className="adp-box" data-testid="adp-history-box">
        <summary>Manual status history ({(adp.history || []).length})</summary>
        <ul className="adp-hist">
          {(adp.history || []).slice().reverse().map((h, i) => (
            <li key={i} data-testid="adp-history-row"><span>{h.event}</span><span className="mut">{fmt(h.at)} · {h.by}</span></li>
          ))}
        </ul>
      </details>

      {/* Final conversion */}
      <div className="adp-convert" data-testid="adp-convert-box">
        {c.convertedToEmployee ? (
          <div className="adp-converted" data-testid="adp-converted"><Check size={16} /> Converted to Employee — {fmt(c.conversion?.at)} · {c.conversion?.by}</div>
        ) : (
          <>
            <button className="onboard-cta" disabled={busy || !canConvert} data-testid="convert-to-employee-btn" onClick={() => setShowConvert(true)}>
              {canConvert ? <>Complete Onboarding / Convert to Employee</> : <><Lock size={14} style={{ verticalAlign: "-2px" }} /> Convert to Employee</>}
            </button>
            {!canConvert && <p className="mut" style={{ marginTop: 6, fontSize: ".78rem" }}>Requires MIHP complete, ADP Setup Complete, and Employment Eligibility Verification complete.</p>}
          </>
        )}
      </div>

      {showConvert && <ConvertModal candidate={c} onClose={() => setShowConvert(false)} onDone={async () => { setShowConvert(false); await reload(); }} />}
    </div>
  );
}
